import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Edit2, Trash2, Package, DollarSign, Users, Clock, TrendingUp, Mail, BarChart3, Inbox, Copy, MapPin, Truck, ChevronRight, KeyRound, BellRing, Tag, MessageSquare, Send, Check, AlertTriangle, Activity, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const RANGE_LABEL = { day: "24h", week: "7d", month: "30d", "12months": "12mo", ytd: "YTD", all: "All" };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "-");

const empty = {
  name: "", category: "Vegetables", packet_size_g: 1.0, description: "",
  stock: 100, image_url: "", featured: false, price: 3.50, cost: 0.0,
  grow_guide: { sun: "", soil: "", depth: "", spacing: "", germinate: "", maturity: "", water: "", when: "", harvest: "", amador_tip: "" },
};
const CATEGORIES = ["Vegetables", "Greens", "Herbs", "Flowers", "Squash", "Beans", "Melons"];
const RANGES = [
  { k: "day", l: "24h" },
  { k: "week", l: "7d" },
  { k: "month", l: "30d" },
  { k: "12months", l: "12mo" },
  { k: "ytd", l: "YTD" },
  { k: "all", l: "All" },
];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [range, setRange] = useState("month");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [popular, setPopular] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [messages, setMessages] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: "", type: "percent", value: "", min_subtotal: "", expires_at: "", active: true });
  const [couponEditing, setCouponEditing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [notifying, setNotifying] = useState(null);
  const [showUnpaid, setShowUnpaid] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [drill, setDrill] = useState({ open: false, type: null, rows: [], loading: false });
  const [cust, setCust] = useState({ open: false, user: null, orders: [], loading: false, resetLink: "" });
  const [analytics, setAnalytics] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const loadAnalytics = async (r = range) => {
    const [s, pop, an] = await Promise.all([
      api.get(`/admin/stats?range=${r}`),
      api.get(`/admin/popular?range=${r}`),
      api.get(`/admin/analytics?range=${r}`),
    ]);
    setStats(s.data); setPopular(pop.data); setAnalytics(an.data);
  };
  const loadAll = async () => {
    const [p, o, req, c, wl, nl, cp, msg] = await Promise.all([
      api.get("/products"), api.get("/admin/orders"), api.get("/admin/seed-requests"), api.get("/admin/customers"), api.get("/admin/stock-notifications"), api.get("/admin/newsletter"), api.get("/admin/coupons"), api.get("/admin/contact-messages"),
    ]);
    setProducts(p.data); setOrders(o.data); setRequests(req.data); setCustomers(c.data); setWaitlist(wl.data); setNewsletter(nl.data); setCoupons(cp.data); setMessages(msg.data);
    await loadAnalytics();
  };

  const resetCouponForm = () => {
    setCouponForm({ code: "", type: "percent", value: "", min_subtotal: "", expires_at: "", active: true });
    setCouponEditing(null);
  };

  const editCoupon = (c) => {
    setCouponEditing(c.coupon_id);
    setCouponForm({
      code: c.code,
      type: c.type,
      value: String(c.value),
      min_subtotal: c.min_subtotal ? String(c.min_subtotal) : "",
      expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
      active: c.active,
    });
  };

  const saveCoupon = async () => {
    const body = {
      code: couponForm.code.trim().toUpperCase(),
      type: couponForm.type,
      value: parseFloat(couponForm.value),
      min_subtotal: couponForm.min_subtotal ? parseFloat(couponForm.min_subtotal) : 0,
      active: couponForm.active,
      expires_at: couponForm.expires_at ? new Date(couponForm.expires_at + "T23:59:59").toISOString() : null,
    };
    if (!body.code || !body.value || body.value <= 0) {
      toast.error("Enter a code and a value greater than 0");
      return;
    }
    try {
      if (couponEditing) {
        const { data } = await api.put(`/admin/coupons/${couponEditing}`, body);
        setCoupons((cs) => cs.map((c) => (c.coupon_id === couponEditing ? data : c)));
        toast.success("Coupon updated");
      } else {
        const { data } = await api.post("/admin/coupons", body);
        setCoupons((cs) => [data, ...cs]);
        toast.success("Coupon created");
      }
      resetCouponForm();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteCoupon = async (coupon_id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await api.delete(`/admin/coupons/${coupon_id}`);
      setCoupons((cs) => cs.filter((c) => c.coupon_id !== coupon_id));
      if (couponEditing === coupon_id) resetCouponForm();
      toast.success("Coupon deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportNewsletter = () => {
    const rows = [["Email", "Subscribed"], ...newsletter.map((s) => [s.email, s.subscribed_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "newsletter-subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const removeWaitlist = async (notify_id) => {
    try {
      await api.delete(`/admin/stock-notifications/${notify_id}`);
      setWaitlist((w) => w.filter((x) => x.notify_id !== notify_id));
      toast.success("Removed from waitlist");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markMessageRead = async (message_id) => {
    try {
      await api.put(`/admin/contact-messages/${message_id}/read`);
      setMessages((m) => m.map((x) => (x.message_id === message_id ? { ...x, read: true } : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleFulfill = async (order_id, fulfilled) => {
    try {
      await api.put(`/admin/orders/${order_id}/fulfill`, { fulfilled });
      setOrders((os) => os.map((o) => (o.order_id === order_id ? { ...o, fulfilled, fulfilled_at: fulfilled ? new Date().toISOString() : null } : o)));
      toast.success(fulfilled ? "Order marked as fulfilled" : "Order marked unfulfilled");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const notifyWaiters = async (product_id, count) => {
    if (!window.confirm(`Send a "back in stock" email to ${count} ${count === 1 ? "person" : "people"} waiting on this seed?`)) return;
    setNotifying(product_id);
    try {
      const { data } = await api.post("/admin/stock-notifications/notify", { product_id });
      toast.success(`Notified ${data.sent} waiter${data.sent !== 1 ? "s" : ""}${data.failed ? ` · ${data.failed} failed` : ""}.`);
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setNotifying(null); }
  };

  const openDrill = async (type) => {
    setDrill({ open: true, type, rows: [], loading: true });
    try {
      const { data } = await api.get(`/admin/drilldown?type=${type}&range=${range}`);
      setDrill({ open: true, type, rows: data.rows, loading: false });
    } catch (e) {
      toast.error(formatApiError(e));
      setDrill({ open: false, type: null, rows: [], loading: false });
    }
  };

  const openCustomer = async (user_id) => {
    setCust({ open: true, user: null, orders: [], loading: true, resetLink: "" });
    try {
      const { data } = await api.get(`/admin/customers/${user_id}/orders`);
      setCust({ open: true, user: data.user, orders: data.orders, loading: false, resetLink: "" });
    } catch (e) {
      toast.error(formatApiError(e));
      setCust({ open: false, user: null, orders: [], loading: false, resetLink: "" });
    }
  };

  const genResetLink = async (user_id) => {
    try {
      const { data } = await api.post(`/admin/users/${user_id}/reset-password-link`);
      setCust((c) => ({ ...c, resetLink: data.reset_url }));
      toast.success(data.emailed ? "Reset link generated & emailed" : "Reset link generated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link);
    toast.success("Link copied");
  };

  useEffect(() => { loadAll().catch(() => toast.error("Failed to load")); }, []);
  useEffect(() => { loadAnalytics(range).catch(() => {}); /* eslint-disable-next-line */ }, [range]);

  const startEdit = (p) => {
    setEditing(p.product_id); setTab("products");
    setForm({
      name: p.name, category: p.category, packet_size_g: p.packet_size_g,
      description: p.description || "", stock: p.stock, image_url: p.image_url || "",
      featured: !!p.featured, price: p.price ?? 3.50, cost: p.cost ?? 0.0,
      grow_guide: { ...empty.grow_guide, ...(p.grow_guide || {}) },
    });
  };
  const cancelEdit = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        packet_size_g: parseFloat(form.packet_size_g),
        stock: parseInt(form.stock),
        price: parseFloat(form.price),
        cost: parseFloat(form.cost),
      };
      if (editing) await api.put(`/products/${editing}`, body);
      else await api.post("/products", body);
      toast.success(editing ? "Updated" : "Created");
      cancelEdit(); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const importCosts = async (file) => {
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/import-costs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Updated ${data.updated} product cost${data.updated === 1 ? "" : "s"}`);
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const guideField = (k, label, type = "text") => (
    <div>
      <label className="text-xs tracking-wider-2 uppercase text-earth">{label}</label>
      {type === "textarea" ? (
        <textarea rows={2} value={form.grow_guide[k] || ""} onChange={(e) => setForm({ ...form, grow_guide: { ...form.grow_guide, [k]: e.target.value } })} className="w-full bg-transparent border border-darkRoast/20 p-2 focus:outline-none focus:border-sage rounded-sm text-sm" data-testid={`form-guide-${k}`} />
      ) : (
        <input value={form.grow_guide[k] || ""} onChange={(e) => setForm({ ...form, grow_guide: { ...form.grow_guide, [k]: e.target.value } })} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage text-sm" data-testid={`form-guide-${k}`} />
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
        <div>
          <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">Workshop</div>
          <h1 className="font-display text-5xl text-darkRoast">Admin.</h1>
        </div>
        <div className="flex gap-1 border border-darkRoast/15 rounded-full p-1" data-testid="range-filter">
          {RANGES.map((r) => (
            <button key={r.k} onClick={() => setRange(r.k)}
              className={`px-3 py-1.5 rounded-full text-[11px] tracking-wider-2 uppercase ${range === r.k ? "bg-darkRoast text-cream" : "text-earth hover:text-darkRoast"}`}
              data-testid={`range-${r.k}`}
            >{r.l}</button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-darkRoast/10 mb-8 overflow-x-auto">
        {[
          { k: "overview", l: "Overview", icon: BarChart3 },
          { k: "analytics", l: "Analytics", icon: Activity },
          { k: "products", l: "Products", icon: Package },
          { k: "orders", l: "Orders", icon: DollarSign },
          { k: "customers", l: "Customers", icon: Users },
          { k: "waitlist", l: "Waitlist", icon: BellRing },
          { k: "newsletter", l: "Newsletter", icon: Mail },
          { k: "coupons", l: "Coupons", icon: Tag },
          { k: "messages", l: "Messages", icon: MessageSquare },
          { k: "requests", l: "Requests", icon: Inbox },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-xs tracking-wider-2 uppercase border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap ${tab === t.k ? "border-sage text-darkRoast" : "border-transparent text-earth hover:text-darkRoast"}`}
            data-testid={`tab-${t.k}`}>
            <t.icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {t.l}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { icon: DollarSign, label: "Revenue", val: `$${stats.revenue.toFixed(2)}`, sub: `${stats.total_orders} orders`, drill: "revenue" },
              { icon: TrendingUp, label: "Est. Profit", val: `$${stats.profit.toFixed(2)}`, sub: `COGS $${stats.cogs.toFixed(2)}`, drill: "profit" },
              { icon: Package, label: "Units sold", val: stats.units_sold, sub: `${stats.total_products} SKUs`, drill: "units" },
              { icon: Users, label: "Customers", val: stats.total_customers, sub: `${stats.new_requests} new requests`, goto: "customers" },
            ].map((s) => (
              <button key={s.label} onClick={() => s.drill ? openDrill(s.drill) : setTab(s.goto)} className="text-left border border-darkRoast/10 p-6 rounded-sm hover:border-sage transition-colors" data-testid={`stat-${s.label.toLowerCase().replace(/[. ]/g, '-')}`}>
                <s.icon className="w-5 h-5 text-sage mb-3" strokeWidth={1.5} />
                <div className="text-xs tracking-wider-2 uppercase text-earth">{s.label}</div>
                <div className="font-display text-3xl text-darkRoast mt-1">{s.val}</div>
                <div className="text-xs text-earth mt-1">{s.sub}</div>
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="border border-darkRoast/10 p-6 rounded-sm" data-testid="popular-seeds-card">
              <h3 className="font-display text-2xl text-darkRoast mb-1">Popular seeds</h3>
              <div className="text-xs text-earth mb-5">Top sellers · {RANGES.find(r => r.k === range)?.l}</div>
              {popular.length === 0 ? (
                <div className="text-earth text-sm py-8 text-center">No sales in this period yet.</div>
              ) : (
                <ol className="space-y-2">
                  {popular.map((p, i) => {
                    const prod = products.find(x => x.product_id === p.product_id);
                    return (
                      <li key={p.product_id}>
                        <button onClick={() => prod && startEdit(prod)} className="w-full flex items-center gap-3 text-sm text-left px-3 py-2 rounded-sm hover:bg-cream transition-colors" data-testid={`popular-${p.product_id}`}>
                          <span className="font-display text-xl text-sage w-6">{i + 1}</span>
                          <span className="flex-1 truncate text-darkRoast">{p.name}</span>
                          <span className="text-earth text-xs">{p.units} sold</span>
                          <span className="font-medium text-darkRoast w-20 text-right">${p.revenue.toFixed(2)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
            <div className="border border-darkRoast/10 p-6 rounded-sm">
              <h3 className="font-display text-2xl text-darkRoast mb-1">Quick actions</h3>
              <div className="text-xs text-earth mb-5">Common admin tasks</div>
              <div className="space-y-2 text-sm">
                <button onClick={() => { cancelEdit(); setTab("products"); }} className="w-full text-left px-4 py-3 border border-darkRoast/10 rounded-sm hover:border-sage hover:text-sage" data-testid="qa-add-product">+ Add new product</button>
                <button onClick={() => setTab("requests")} className="w-full text-left px-4 py-3 border border-darkRoast/10 rounded-sm hover:border-sage hover:text-sage" data-testid="qa-view-requests">View seed requests ({requests.length})</button>
                <button onClick={() => setTab("orders")} className="w-full text-left px-4 py-3 border border-darkRoast/10 rounded-sm hover:border-sage hover:text-sage" data-testid="qa-view-orders">View all orders ({orders.length})</button>
              </div>
            </div>
          </div>

          {(() => {
            const LOW = 10;
            const low = products.filter((p) => (p.stock ?? 0) <= LOW).sort((a, b) => (a.stock ?? 0) - (b.stock ?? 0));
            if (low.length === 0) return null;
            return (
              <div className="border border-terracotta/30 bg-terracotta/5 p-6 rounded-sm mt-6" data-testid="low-stock-card">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
                  <h3 className="font-display text-2xl text-darkRoast">Low stock</h3>
                </div>
                <div className="text-xs text-earth mb-5">{low.length} seed{low.length !== 1 ? "s" : ""} at or below {LOW} packets, restock soon</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {low.map((p) => (
                    <button
                      key={p.product_id}
                      onClick={() => startEdit(p)}
                      className="flex items-center justify-between gap-3 text-left px-4 py-3 bg-cream border border-darkRoast/10 rounded-sm hover:border-sage"
                      data-testid={`low-stock-${p.product_id}`}
                    >
                      <span className="text-sm text-darkRoast truncate">{p.name}</span>
                      <span className={`text-xs font-medium shrink-0 ${(p.stock ?? 0) === 0 ? "text-terracotta" : "text-earth"}`}>
                        {(p.stock ?? 0) === 0 ? "Sold out" : `${p.stock} left`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {tab === "analytics" && (
        <div className="space-y-8" data-testid="analytics-tab">
          {!analytics ? (
            <div className="text-earth text-sm py-12 text-center">Loading analytics…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: Activity, label: "Page views", val: analytics.pageviews },
                  { icon: Users, label: "Unique visitors", val: analytics.unique_visitors },
                  { icon: Package, label: "Product views", val: analytics.product_views },
                  { icon: DollarSign, label: "Conversion rate", val: `${analytics.conversion_rate}%` },
                ].map((s) => (
                  <div key={s.label} className="border border-darkRoast/10 p-6 rounded-sm" data-testid={`analytics-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <s.icon className="w-5 h-5 text-sage mb-3" strokeWidth={1.5} />
                    <div className="text-xs tracking-wider-2 uppercase text-earth">{s.label}</div>
                    <div className="font-display text-3xl text-darkRoast mt-1">{s.val}</div>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <div className="border border-darkRoast/10 p-6 rounded-sm" data-testid="analytics-funnel">
                  <h3 className="font-display text-2xl text-darkRoast mb-1">Conversion funnel</h3>
                  <div className="text-xs text-earth mb-5">Visitor journey · {RANGES.find(r => r.k === range)?.l}</div>
                  <div className="space-y-3">
                    {analytics.funnel.map((f, i) => {
                      const top = analytics.funnel[0].count || 1;
                      const pct = Math.round((f.count / top) * 100);
                      return (
                        <div key={f.step} data-testid={`funnel-${i}`}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-darkRoast">{f.step}</span>
                            <span className="text-earth">{f.count} · {pct}%</span>
                          </div>
                          <div className="h-2 bg-cream rounded-full overflow-hidden">
                            <div className="h-full bg-sage rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-6 pt-5 border-t border-darkRoast/10 text-center">
                    <div>
                      <div className="font-display text-2xl text-darkRoast">{analytics.add_to_cart_rate}%</div>
                      <div className="text-[11px] tracking-wider-2 uppercase text-earth">View → Cart</div>
                    </div>
                    <div>
                      <div className="font-display text-2xl text-darkRoast">{analytics.checkout_completion_rate}%</div>
                      <div className="text-[11px] tracking-wider-2 uppercase text-earth">Checkout → Paid</div>
                    </div>
                  </div>
                </div>

                <div className="border border-darkRoast/10 p-6 rounded-sm" data-testid="analytics-top-pages">
                  <h3 className="font-display text-2xl text-darkRoast mb-1">Top pages</h3>
                  <div className="text-xs text-earth mb-5">Most viewed · {RANGES.find(r => r.k === range)?.l}</div>
                  {analytics.top_pages.length === 0 ? (
                    <div className="text-earth text-sm py-8 text-center">No page views in this period yet.</div>
                  ) : (
                    <ol className="space-y-1">
                      {analytics.top_pages.map((p, i) => (
                        <li key={p.path} className="flex items-center gap-3 text-sm px-3 py-2 rounded-sm hover:bg-cream">
                          <span className="font-display text-lg text-sage w-6">{i + 1}</span>
                          <span className="flex-1 truncate text-darkRoast">{p.path}</span>
                          <span className="text-earth text-xs">{p.views} views</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "products" && (
        <div className="grid lg:grid-cols-5 gap-10">
          <form onSubmit={save} className="lg:col-span-2 bg-surface p-6 rounded-sm space-y-4 h-fit" data-testid="product-form">
            <h2 className="font-display text-2xl text-darkRoast">{editing ? "Edit product" : "Add product"}</h2>

            <div>
              <label className="text-xs tracking-wider-2 uppercase text-earth">Name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="form-name" />
            </div>
            <div>
              <label className="text-xs tracking-wider-2 uppercase text-earth">Image</label>
              {form.image_url && (
                <div className="mt-2 mb-3 w-32 h-32 bg-cream border border-darkRoast/10 rounded-sm overflow-hidden">
                  <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex gap-2 items-center">
                <label className="cursor-pointer bg-darkRoast text-cream px-4 py-2 rounded-full text-[11px] tracking-wider-2 uppercase hover:bg-sage transition-colors whitespace-nowrap">
                  {uploading ? "Uploading…" : "Upload file"}
                  <input type="file" accept="image/*" className="hidden" data-testid="form-image-upload" disabled={uploading}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 5_000_000) { toast.error("Image too large (5MB max)"); return; }
                      setUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append("file", f);
                        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        setForm((prev) => ({ ...prev, image_url: data.url }));
                        toast.success("Image uploaded");
                      } catch (err) {
                        // Fall back to inline base64 if object storage isn't configured yet
                        const reader = new FileReader();
                        reader.onload = (ev) => setForm((prev) => ({ ...prev, image_url: ev.target.result }));
                        reader.readAsDataURL(f);
                        toast.message("Saved locally (object storage not configured yet)");
                      } finally {
                        setUploading(false);
                        e.target.value = "";
                      }
                    }} />
                </label>
                {form.image_url && (
                  <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="text-xs text-terracotta hover:underline" data-testid="form-image-clear">Clear</button>
                )}
              </div>
              <input value={form.image_url.startsWith("data:") ? "" : form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="…or paste an image URL"
                className="w-full bg-transparent border-b border-darkRoast/20 py-2 mt-3 text-sm focus:outline-none focus:border-sage" data-testid="form-image_url" />
            </div>
            <div>
              <label className="text-xs tracking-wider-2 uppercase text-earth">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="form-category">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="text-xs tracking-wider-2 uppercase text-earth">Packet (g)</label>
                <input type="number" step="0.1" value={form.packet_size_g} onChange={(e) => setForm({ ...form, packet_size_g: e.target.value })} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="form-packet" />
              </div>
              <div>
                <label className="text-xs tracking-wider-2 uppercase text-earth">Stock</label>
                <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="form-stock" />
              </div>
              <div>
                <label className="text-xs tracking-wider-2 uppercase text-earth">Price $</label>
                <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="form-price" />
              </div>
              <div>
                <label className="text-xs tracking-wider-2 uppercase text-earth">Cost $</label>
                <input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="form-cost" />
              </div>
            </div>
            <div>
              <label className="text-xs tracking-wider-2 uppercase text-earth">Description</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-transparent border border-darkRoast/20 p-2 focus:outline-none focus:border-sage rounded-sm" data-testid="form-description" />
            </div>

            <details className="pt-2">
              <summary className="text-xs tracking-wider-2 uppercase text-earth cursor-pointer hover:text-darkRoast">Grow guide (blog content)</summary>
              <div className="space-y-3 pt-4">
                <div className="grid grid-cols-2 gap-3">
                  {guideField("sun", "Sun")}
                  {guideField("soil", "Soil")}
                  {guideField("depth", "Planting depth")}
                  {guideField("spacing", "Spacing")}
                  {guideField("germinate", "Days to germinate")}
                  {guideField("maturity", "Days to maturity")}
                </div>
                {guideField("when", "When to plant", "textarea")}
                {guideField("water", "Watering", "textarea")}
                {guideField("harvest", "Harvest", "textarea")}
                {guideField("amador_tip", "Amador tip", "textarea")}
              </div>
            </details>

            <div className="flex gap-3 pt-3">
              <button type="submit" className="flex-1 bg-darkRoast text-cream py-3 rounded-full text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors" data-testid="form-submit">{editing ? "Save" : "Add"}</button>
              {editing && (
                <button type="button" onClick={cancelEdit} className="px-4 border border-darkRoast/20 rounded-full text-xs tracking-wider-2 uppercase text-darkRoast hover:border-sage" data-testid="form-cancel">Cancel</button>
              )}
            </div>
          </form>

          <div className="lg:col-span-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-surface border border-darkRoast/10 p-4 rounded-sm mb-2" data-testid="cost-import-card">
              <div>
                <div className="text-sm font-medium text-darkRoast">Bulk import costs</div>
                <div className="text-xs text-earth mt-0.5">CSV with a <code>cost</code> column + <code>product_id</code> or <code>name</code>.</div>
                {importResult && (
                  <div className="text-xs text-sage mt-1" data-testid="import-result">
                    Updated {importResult.updated} · skipped {importResult.skipped}
                    {importResult.errors?.length ? ` · ${importResult.errors.length} error(s)` : ""}
                  </div>
                )}
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 bg-darkRoast text-cream px-4 py-2 rounded-full text-[11px] tracking-wider-2 uppercase hover:bg-sage transition-colors whitespace-nowrap">
                <Upload className="w-3.5 h-3.5" strokeWidth={1.5} /> {importing ? "Importing…" : "Upload CSV"}
                <input type="file" accept=".csv,text/csv" className="hidden" disabled={importing} data-testid="cost-import-input"
                  onChange={(e) => { const f = e.target.files?.[0]; importCosts(f); e.target.value = ""; }} />
              </label>
            </div>
            {products.map((p) => (
              <div key={p.product_id} className="flex items-center gap-4 border border-darkRoast/10 p-3 rounded-sm hover:border-sage transition-colors" data-testid={`admin-product-${p.product_id}`}>
                <button onClick={() => startEdit(p)} className="flex items-center gap-4 flex-1 text-left min-w-0" data-testid={`row-edit-${p.product_id}`}>
                  <div className="w-14 h-14 bg-surface rounded-sm overflow-hidden flex-shrink-0">
                    {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-wider-2 uppercase text-sage">{p.category}</div>
                    <div className="font-display text-base text-darkRoast truncate">{p.name}</div>
                    <div className="text-xs text-earth">${(p.price ?? 3.5).toFixed(2)} · {p.packet_size_g}g · Stock {p.stock}{p.cost ? ` · Cost $${Number(p.cost).toFixed(2)}` : ""}</div>
                  </div>
                </button>
                <button onClick={() => startEdit(p)} className="p-2 text-earth hover:text-sage" data-testid={`edit-${p.product_id}`}><Edit2 className="w-4 h-4" strokeWidth={1.5} /></button>
                <button onClick={() => remove(p.product_id)} className="p-2 text-earth hover:text-terracotta" data-testid={`delete-${p.product_id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (() => {
        const visible = orders.filter((o) => showUnpaid || o.payment_status === "paid");
        const hiddenCount = orders.length - orders.filter((o) => o.payment_status === "paid").length;
        return (
          <div className="space-y-3" data-testid="orders-tab">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-earth">{visible.length} {showUnpaid ? "total" : "paid"} order{visible.length !== 1 ? "s" : ""}</div>
              <button
                onClick={() => setShowUnpaid((v) => !v)}
                className="text-[11px] tracking-wider-2 uppercase text-earth hover:text-darkRoast border border-darkRoast/15 rounded-full px-3 py-1.5"
                data-testid="toggle-unpaid-orders"
              >
                {showUnpaid ? "Hide unpaid" : `Show unpaid${hiddenCount ? ` (${hiddenCount})` : ""}`}
              </button>
            </div>
            {visible.length === 0 && <div className="text-earth text-center py-12" data-testid="no-orders">No {showUnpaid ? "" : "paid "}orders yet.</div>}
            {visible.map((o) => (
              <div key={o.order_id} className="border border-darkRoast/10 p-5 rounded-sm" data-testid={`admin-order-${o.order_id}`}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <div className="text-xs tracking-wider-2 uppercase text-earth">Order</div>
                    <div className="font-display text-xl text-darkRoast">#{o.order_id.slice(-8).toUpperCase()}</div>
                    <div className="text-xs text-earth mt-1">{o.user_email} · {new Date(o.created_at).toLocaleDateString()}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {o.fulfillment === "pickup" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tracking-wider-2 uppercase bg-terracotta/15 text-terracotta" data-testid={`order-fulfillment-${o.order_id}`}>
                          <MapPin className="w-3 h-3" strokeWidth={1.5} /> Pickup · {o.pickup_location || "Plymouth, CA"}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tracking-wider-2 uppercase bg-darkRoast/10 text-earth" data-testid={`order-fulfillment-${o.order_id}`}>
                          <Truck className="w-3 h-3" strokeWidth={1.5} /> Ship
                        </span>
                      )}
                      {o.payment_status === "paid" && o.fulfilled && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tracking-wider-2 uppercase bg-sage/15 text-sage" data-testid={`order-fulfilled-${o.order_id}`}>
                          <Check className="w-3 h-3" strokeWidth={2} /> Fulfilled
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-3 py-1 rounded-full text-[10px] tracking-wider-2 uppercase ${o.payment_status === "paid" ? "bg-sage/15 text-sage" : "bg-darkRoast/10 text-earth"}`}>{o.payment_status}</span>
                    <div className="font-medium text-darkRoast mt-1">${o.total.toFixed(2)}</div>
                  </div>
                </div>

                {o.fulfillment !== "pickup" && o.shipping && (
                  <div className="text-sm text-darkRoast mt-3 bg-surface rounded-sm px-3 py-2" data-testid={`order-address-${o.order_id}`}>
                    <div className="text-[10px] tracking-wider-2 uppercase text-earth mb-1">Ship to</div>
                    <div>{o.shipping.name}</div>
                    {o.shipping.address && <div>{o.shipping.address}</div>}
                    <div>{[o.shipping.city, o.shipping.state, o.shipping.zip].filter(Boolean).join(", ")}</div>
                  </div>
                )}

                <div className="text-sm text-earth mt-3 space-y-0.5">
                  {o.items.map((it) => <div key={it.product_id}>{it.name} × {it.quantity}</div>)}
                </div>

                {o.payment_status === "paid" && (
                  <button
                    onClick={() => toggleFulfill(o.order_id, !o.fulfilled)}
                    className={`mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] tracking-wider-2 uppercase transition-colors ${o.fulfilled ? "border border-darkRoast/20 text-earth hover:text-darkRoast" : "bg-darkRoast text-cream hover:bg-sage"}`}
                    data-testid={`fulfill-toggle-${o.order_id}`}
                  >
                    <Check className="w-3.5 h-3.5" strokeWidth={2} /> {o.fulfilled ? "Mark unfulfilled" : "Mark as fulfilled"}
                  </button>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {tab === "customers" && (
        <div className="space-y-2" data-testid="customers-tab">
          {customers.length === 0 && <div className="text-earth text-center py-12" data-testid="no-customers">No customers yet.</div>}
          {customers.length > 0 && (
            <div className="hidden sm:grid grid-cols-12 gap-3 px-4 text-[10px] tracking-wider-2 uppercase text-earth pb-2">
              <div className="col-span-4">Customer</div>
              <div className="col-span-2 text-right">Orders</div>
              <div className="col-span-2 text-right">Avg order</div>
              <div className="col-span-2 text-right">Lifetime</div>
              <div className="col-span-2 text-right">Last order</div>
            </div>
          )}
          {customers.map((c) => (
            <button key={c.user_id} onClick={() => openCustomer(c.user_id)} className="w-full grid grid-cols-2 sm:grid-cols-12 gap-3 items-center border border-darkRoast/10 p-4 rounded-sm hover:border-sage transition-colors text-left" data-testid={`customer-${c.user_id}`}>
              <div className="col-span-2 sm:col-span-4 min-w-0">
                <div className="font-display text-base text-darkRoast truncate">{c.name || "-"}</div>
                <div className="text-xs text-earth truncate">{c.email}</div>
              </div>
              <div className="sm:col-span-2 sm:text-right text-sm text-darkRoast"><span className="sm:hidden text-earth text-xs">Orders: </span>{c.total_orders}</div>
              <div className="sm:col-span-2 sm:text-right text-sm text-darkRoast"><span className="sm:hidden text-earth text-xs">Avg: </span>${c.avg_order_value.toFixed(2)}</div>
              <div className="sm:col-span-2 sm:text-right text-sm font-medium text-darkRoast"><span className="sm:hidden text-earth text-xs">Lifetime: </span>${c.lifetime_spend.toFixed(2)}</div>
              <div className="sm:col-span-2 sm:text-right text-xs text-earth flex items-center sm:justify-end gap-1">{fmtDate(c.last_order_date)}<ChevronRight className="w-3.5 h-3.5 hidden sm:block" strokeWidth={1.5} /></div>
            </button>
          ))}
        </div>
      )}

      {tab === "waitlist" && (() => {
        const pending = waitlist.filter((w) => (w.status || "pending") === "pending");
        return (
          <div className="space-y-6" data-testid="waitlist-tab">
            {pending.length === 0 && <div className="text-earth text-center py-12" data-testid="no-waitlist">No restock signups yet.</div>}
            {pending.length > 0 && Object.entries(
              pending.reduce((acc, w) => { (acc[w.product_name] = acc[w.product_name] || []).push(w); return acc; }, {})
            ).sort((a, b) => b[1].length - a[1].length).map(([name, items]) => (
              <div key={name} className="border border-darkRoast/10 rounded-sm overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-surface">
                  <div className="font-display text-lg text-darkRoast truncate">{name || "Unknown seed"}</div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs tracking-wider-2 uppercase text-sage">{items.length} waiting</span>
                    <button
                      onClick={() => notifyWaiters(items[0].product_id, items.length)}
                      disabled={notifying === items[0].product_id}
                      className="inline-flex items-center gap-2 bg-darkRoast text-cream px-4 py-2 rounded-full text-[11px] tracking-wider-2 uppercase hover:bg-sage transition-colors disabled:opacity-50"
                      data-testid={`notify-waiters-${items[0].product_id}`}
                    >
                      <Send className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {notifying === items[0].product_id ? "Sending…" : `Notify all (${items.length})`}
                    </button>
                  </div>
                </div>
                <div className="divide-y divide-darkRoast/5">
                  {items.map((w) => (
                    <div key={w.notify_id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`waitlist-${w.notify_id}`}>
                      <span className="text-darkRoast truncate">{w.email}</span>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="text-xs text-earth">{fmtDate(w.created_at)}</span>
                        <button onClick={() => removeWaitlist(w.notify_id)} className="text-earth hover:text-terracotta" data-testid={`waitlist-remove-${w.notify_id}`}>
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {tab === "newsletter" && (
        <div className="space-y-3" data-testid="newsletter-tab">
          <div className="flex items-center justify-between pb-2">
            <div className="text-sm text-earth" data-testid="newsletter-count">
              {newsletter.length} subscriber{newsletter.length !== 1 ? "s" : ""}
            </div>
            {newsletter.length > 0 && (
              <button
                onClick={exportNewsletter}
                className="inline-flex items-center gap-2 border border-darkRoast/20 text-darkRoast rounded-full px-4 py-2 text-[11px] tracking-wider-2 uppercase hover:bg-darkRoast hover:text-cream transition-colors"
                data-testid="newsletter-export"
              >
                Export CSV
              </button>
            )}
          </div>
          {newsletter.length === 0 && <div className="text-earth text-center py-12" data-testid="no-newsletter">No subscribers yet.</div>}
          {newsletter.map((s) => (
            <div key={s.email} className="flex items-center justify-between border border-darkRoast/10 p-4 rounded-sm text-sm" data-testid={`newsletter-${s.email}`}>
              <span className="text-darkRoast truncate">{s.email}</span>
              <span className="text-xs text-earth shrink-0">{fmtDate(s.subscribed_at)}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "coupons" && (
        <div className="grid lg:grid-cols-3 gap-8" data-testid="coupons-tab">
          <div className="lg:col-span-1">
            <div className="border border-darkRoast/10 p-6 rounded-sm space-y-4">
              <h3 className="font-display text-2xl text-darkRoast">{couponEditing ? "Edit coupon" : "New coupon"}</h3>
              <div>
                <label className="text-[10px] tracking-wider-2 uppercase text-earth">Code</label>
                <input
                  value={couponForm.code}
                  onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                  placeholder="SPRING10"
                  className="w-full bg-transparent border-b border-darkRoast/20 px-1 py-2 text-sm uppercase focus:outline-none focus:border-sage"
                  data-testid="coupon-code-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-wider-2 uppercase text-earth">Type</label>
                  <select
                    value={couponForm.type}
                    onChange={(e) => setCouponForm({ ...couponForm, type: e.target.value })}
                    className="w-full bg-transparent border-b border-darkRoast/20 px-1 py-2 text-sm focus:outline-none focus:border-sage"
                    data-testid="coupon-type-select"
                  >
                    <option value="percent">Percent %</option>
                    <option value="fixed">Fixed $</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-wider-2 uppercase text-earth">{couponForm.type === "percent" ? "Percent off" : "Amount off ($)"}</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={couponForm.value}
                    onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })}
                    placeholder={couponForm.type === "percent" ? "10" : "5.00"}
                    className="w-full bg-transparent border-b border-darkRoast/20 px-1 py-2 text-sm focus:outline-none focus:border-sage"
                    data-testid="coupon-value-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-wider-2 uppercase text-earth">Min. subtotal ($)</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={couponForm.min_subtotal}
                    onChange={(e) => setCouponForm({ ...couponForm, min_subtotal: e.target.value })}
                    placeholder="0"
                    className="w-full bg-transparent border-b border-darkRoast/20 px-1 py-2 text-sm focus:outline-none focus:border-sage"
                    data-testid="coupon-min-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-wider-2 uppercase text-earth">Expires (optional)</label>
                  <input
                    type="date"
                    value={couponForm.expires_at}
                    onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value })}
                    className="w-full bg-transparent border-b border-darkRoast/20 px-1 py-2 text-sm focus:outline-none focus:border-sage"
                    data-testid="coupon-expires-input"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-earth cursor-pointer">
                <input
                  type="checkbox"
                  checked={couponForm.active}
                  onChange={(e) => setCouponForm({ ...couponForm, active: e.target.checked })}
                  data-testid="coupon-active-checkbox"
                />
                Active
              </label>
              <div className="flex gap-2 pt-2">
                <button onClick={saveCoupon} className="flex-1 bg-darkRoast text-cream py-3 rounded-full text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors" data-testid="coupon-save-btn">
                  {couponEditing ? "Update" : "Create"}
                </button>
                {couponEditing && (
                  <button onClick={resetCouponForm} className="px-4 border border-darkRoast/20 rounded-full text-xs tracking-wider-2 uppercase hover:border-sage" data-testid="coupon-cancel-btn">
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-3">
            {coupons.length === 0 && <div className="text-earth text-center py-12" data-testid="no-coupons">No coupons yet. Create one to start offering discounts.</div>}
            {coupons.map((c) => {
              const expired = c.expires_at && new Date(c.expires_at) < new Date();
              return (
                <div key={c.coupon_id} className="flex items-center justify-between border border-darkRoast/10 p-4 rounded-sm" data-testid={`coupon-${c.code}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-xl text-darkRoast">{c.code}</span>
                      <span className="text-xs tracking-wider-2 uppercase bg-sage/10 text-sage px-2 py-0.5 rounded-full">
                        {c.type === "percent" ? `${c.value}% off` : `$${c.value.toFixed(2)} off`}
                      </span>
                      {!c.active && <span className="text-[10px] tracking-wider-2 uppercase bg-earth/10 text-earth px-2 py-0.5 rounded-full">Inactive</span>}
                      {expired && <span className="text-[10px] tracking-wider-2 uppercase bg-terracotta/10 text-terracotta px-2 py-0.5 rounded-full">Expired</span>}
                    </div>
                    <div className="text-xs text-earth mt-1">
                      {c.min_subtotal > 0 ? `Min $${c.min_subtotal.toFixed(2)} · ` : ""}
                      Used {c.usage_count}×
                      {c.expires_at ? ` · expires ${fmtDate(c.expires_at)}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => editCoupon(c)} className="p-2 text-earth hover:text-sage" data-testid={`coupon-edit-${c.code}`} aria-label="Edit">
                      <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button onClick={() => deleteCoupon(c.coupon_id)} className="p-2 text-earth hover:text-terracotta" data-testid={`coupon-delete-${c.code}`} aria-label="Delete">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "messages" && (
        <div className="space-y-3" data-testid="messages-tab">
          {messages.length === 0 && <div className="text-earth text-center py-12" data-testid="no-messages">No contact messages yet.</div>}
          {messages.map((m) => (
            <div key={m.message_id} className={`border p-5 rounded-sm ${m.read ? "border-darkRoast/10" : "border-sage bg-sage/5"}`} data-testid={`admin-message-${m.message_id}`}>
              <div className="flex flex-wrap justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-xs tracking-wider-2 uppercase text-sage">{m.subject || "No subject"}</div>
                    {!m.read && <span className="text-[10px] tracking-wider-2 uppercase bg-sage text-cream px-2 py-0.5 rounded-full" data-testid={`message-unread-${m.message_id}`}>New</span>}
                  </div>
                  <div className="font-display text-xl text-darkRoast mt-1">{m.name}</div>
                  <div className="text-xs text-earth mt-1">{m.email} · {new Date(m.created_at).toLocaleString()}</div>
                  <p className="text-sm text-earth mt-3 whitespace-pre-wrap">{m.message}</p>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <a
                    href={`mailto:${m.email}?subject=${encodeURIComponent("Re: " + (m.subject || "Your message to Rooted in Amador"))}`}
                    className="inline-flex items-center gap-2 bg-darkRoast text-cream px-4 py-2.5 rounded-full text-[11px] tracking-wider-2 uppercase hover:bg-sage transition-colors"
                    data-testid={`message-reply-${m.message_id}`}
                  >
                    <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> Reply
                  </a>
                  {!m.read && (
                    <button
                      onClick={() => markMessageRead(m.message_id)}
                      className="text-[11px] tracking-wider-2 uppercase text-earth hover:text-darkRoast"
                      data-testid={`message-mark-read-${m.message_id}`}
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {tab === "requests" && (
        <div className="space-y-3">
          {requests.length === 0 && <div className="text-earth text-center py-12" data-testid="no-requests">No seed requests yet.</div>}
          {requests.map((r) => (
            <div key={r.request_id} className="border border-darkRoast/10 p-5 rounded-sm" data-testid={`admin-request-${r.request_id}`}>
              <div className="flex flex-wrap justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs tracking-wider-2 uppercase text-sage">Requested seed</div>
                  <div className="font-display text-2xl text-darkRoast">{r.seed}</div>
                  <div className="text-xs text-earth mt-2">From <span className="text-darkRoast">{r.name || "Anonymous"}</span> · {new Date(r.created_at).toLocaleString()}</div>
                </div>
                <a
                  href={`mailto:${r.email}?subject=Your%20seed%20request%20at%20Rooted%20in%20Amador&body=Hi%20${encodeURIComponent(r.name || "there")}%2C%0A%0AThanks%20for%20your%20request%20for%20${encodeURIComponent(r.seed)}.%20`}
                  className="inline-flex items-center gap-2 bg-darkRoast text-cream px-4 py-2.5 rounded-full text-[11px] tracking-wider-2 uppercase hover:bg-sage transition-colors"
                  data-testid={`mailto-${r.request_id}`}
                >
                  <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {r.email}
                </a>
              </div>
              {r.notes && <div className="text-sm text-earth mt-3 italic">"{r.notes}"</div>}
            </div>
          ))}
        </div>
      )}
      {/* Stat-card drilldown modal */}
      <Dialog open={drill.open} onOpenChange={(o) => !o && setDrill({ open: false, type: null, rows: [], loading: false })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-cream border-darkRoast/10" data-testid="drilldown-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-darkRoast capitalize">
              {drill.type === "units" ? "Units sold" : drill.type} breakdown
              <span className="block text-xs tracking-wider-2 uppercase text-sage mt-1">{RANGE_LABEL[range]} · paid orders</span>
            </DialogTitle>
          </DialogHeader>
          {drill.loading ? (
            <div className="text-earth text-sm py-10 text-center">Loading…</div>
          ) : drill.rows.length === 0 ? (
            <div className="text-earth text-sm py-10 text-center" data-testid="drilldown-empty">No data in this period.</div>
          ) : drill.type === "units" ? (
            <table className="w-full text-sm" data-testid="drilldown-units-table">
              <thead><tr className="text-[10px] tracking-wider-2 uppercase text-earth border-b border-darkRoast/10">
                <th className="text-left py-2">Product</th><th className="text-left py-2">Category</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Revenue</th><th className="text-right py-2">Date</th>
              </tr></thead>
              <tbody>
                {drill.rows.map((r, i) => (
                  <tr key={i} className="border-b border-darkRoast/5">
                    <td className="py-2 text-darkRoast">{r.product}</td>
                    <td className="py-2 text-earth">{r.category}</td>
                    <td className="py-2 text-right text-darkRoast">{r.quantity}</td>
                    <td className="py-2 text-right text-darkRoast">${r.revenue.toFixed(2)}</td>
                    <td className="py-2 text-right text-earth">{fmtDate(r.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="drilldown-orders-table">
              <thead><tr className="text-[10px] tracking-wider-2 uppercase text-earth border-b border-darkRoast/10">
                <th className="text-left py-2">Order</th><th className="text-left py-2">Customer</th><th className="text-right py-2">Items</th><th className="text-right py-2">Total</th>
                {drill.type === "profit" && <><th className="text-right py-2">Cost</th><th className="text-right py-2">Profit</th></>}
                <th className="text-right py-2">Date</th>
              </tr></thead>
              <tbody>
                {drill.rows.map((r) => (
                  <tr key={r.order_id} className="border-b border-darkRoast/5">
                    <td className="py-2 text-darkRoast">#{r.order_id.slice(-8).toUpperCase()}</td>
                    <td className="py-2 text-earth truncate max-w-[160px]">{r.customer}</td>
                    <td className="py-2 text-right text-darkRoast">{r.item_count}</td>
                    <td className="py-2 text-right text-darkRoast">${r.total.toFixed(2)}</td>
                    {drill.type === "profit" && <><td className="py-2 text-right text-earth">${r.cost.toFixed(2)}</td><td className="py-2 text-right text-sage">${r.profit.toFixed(2)}</td></>}
                    <td className="py-2 text-right text-earth">{fmtDate(r.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>

      {/* Customer detail modal */}
      <Dialog open={cust.open} onOpenChange={(o) => !o && setCust({ open: false, user: null, orders: [], loading: false, resetLink: "" })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-cream border-darkRoast/10" data-testid="customer-modal">
          {cust.loading ? (
            <div className="text-earth text-sm py-10 text-center">Loading…</div>
          ) : cust.user && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-darkRoast">{cust.user.name || "Customer"}</DialogTitle>
              </DialogHeader>
              <div className="text-sm text-earth -mt-2">{cust.user.email} · joined {fmtDate(cust.user.created_at)}</div>

              <div className="flex flex-wrap items-center gap-3 py-3 border-y border-darkRoast/10">
                <button onClick={() => genResetLink(cust.user.user_id)} className="inline-flex items-center gap-2 bg-darkRoast text-cream px-4 py-2 rounded-full text-[11px] tracking-wider-2 uppercase hover:bg-sage transition-colors" data-testid="gen-reset-link-btn">
                  <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} /> Generate password reset link
                </button>
              </div>
              {cust.resetLink && (
                <div className="flex items-center gap-2 bg-surface p-3 rounded-sm" data-testid="reset-link-box">
                  <input readOnly value={cust.resetLink} className="flex-1 bg-transparent text-xs text-earth focus:outline-none truncate" data-testid="reset-link-value" />
                  <button onClick={() => copyLink(cust.resetLink)} className="text-sage hover:text-darkRoast" data-testid="copy-reset-link"><Copy className="w-4 h-4" strokeWidth={1.5} /></button>
                </div>
              )}

              <div className="text-xs tracking-wider-2 uppercase text-sage mt-2">Order history ({cust.orders.length})</div>
              {cust.orders.length === 0 ? (
                <div className="text-earth text-sm py-6 text-center" data-testid="customer-no-orders">No orders yet.</div>
              ) : (
                <div className="space-y-2">
                  {cust.orders.map((o) => (
                    <div key={o.order_id} className="border border-darkRoast/10 p-3 rounded-sm" data-testid={`customer-order-${o.order_id}`}>
                      <div className="flex justify-between items-center">
                        <div className="font-display text-base text-darkRoast">#{o.order_id.slice(-8).toUpperCase()}</div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] tracking-wider-2 uppercase ${o.payment_status === "paid" ? "bg-sage/15 text-sage" : "bg-darkRoast/10 text-earth"}`}>{o.payment_status}</span>
                          <span className="font-medium text-darkRoast text-sm">${o.total.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="text-xs text-earth mt-1">{fmtDate(o.created_at)} · {o.fulfillment === "pickup" ? "Pickup" : "Ship"}</div>
                      <div className="text-xs text-earth mt-1">{o.items.map((it) => `${it.name} ×${it.quantity}`).join(", ")}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
